/**
 * Prompt Builder views (Phase 2 + Phase 3 UI).
 *
 * Two top-level renderers used by the agent prompt editor and the
 * chain-prompts editor:
 *
 *   <AuthoredView definition={...} />
 *     Sectioned, table-based render of the structured AgentDefinition.
 *     This is the prompt-builder shape — Persona, Entry, Rules table,
 *     Steps table, Exit, Tools, Tool Gates table.
 *
 *   <CompiledView prompt={...} ruleMap={...} />
 *     The LLM-eye view: rule map (universal/framework/agent merge) plus
 *     the final compiled prompt that the runtime sends to Gemini.
 *
 * Plus small UI primitives (Section, Table, FieldRow, SeverityBadge, Chip)
 * shared across both views and reusable in other pages.
 */

import React from "react";

// ─────────────────────────────────────────────────────────────────────
// AUTHORED VIEW — sectioned, table-based renderer for a structured definition.
// ─────────────────────────────────────────────────────────────────────
export function AuthoredView({ definition, toolCatalog = null }) {
  if (!definition) return <div className="muted">No structured definition.</div>;
  const {
    persona = {},
    entry = {},
    rules = [],
    steps = [],
    exit = {},
    tools = [],
    tool_gates = [],
  } = definition;

  // Index gates by tool name so the Tools table can show preconditions inline.
  const gatesByTool = (tool_gates || []).reduce((acc, g) => {
    acc[g.tool] = g;
    return acc;
  }, {});
  const lookupCatalog = (name) => (toolCatalog && toolCatalog[name]) || null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {/* 1. PERSONA */}
      <Section title="Persona" subtitle="Who you are. What your job is. Stay-in-role guardrail.">
        <FieldRow label="Role" value={persona.role} />
        <FieldRow label="Job" value={persona.job} multiline />
        <FieldRow label="Guardrail" value={persona.guardrail} multiline />
      </Section>

      {/* 2. TOOLS — promoted to position 2 (capabilities are what an
          agent CAN do; reviewers want this prominent). Each tool row
          shows its description (from the catalog when available),
          catalog presence, and any gates that govern when it fires. */}
      <Section
        title={`Tools (${tools.length})`}
        subtitle="What this agent can call. The LLM sees ONLY these. Each tool may have gate preconditions."
      >
        {tools.length === 0 ? (
          <div className="muted small">(no tools assigned)</div>
        ) : (
          <Table
            head={["Tool", "Description", "Catalog", "Gates (preconditions)"]}
            rows={tools.map((t) => {
              const catalog = lookupCatalog(t);
              const gate = gatesByTool[t];
              return [
                <Chip key="t" kind="tool">{t}</Chip>,
                <div key="d" style={{ fontSize: 12 }}>
                  {catalog?.description
                    ? catalog.description
                    : <span className="muted small">{toolCatalog ? "(no catalog entry)" : "(catalog not loaded)"}</span>}
                </div>,
                <span key="c" style={{ fontSize: 11 }}>
                  {catalog
                    ? <Chip kind="state">✓ in catalog</Chip>
                    : (toolCatalog ? <Chip kind="wait">⚠ not in catalog</Chip> : <span className="muted small">—</span>)}
                </span>,
                <div key="g" style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {gate?.require?.length > 0
                    ? gate.require.map((req) => <Chip key={req} kind="context">{req}</Chip>)
                    : <span className="muted small">always available</span>}
                  {gate?.mode === "warn" && <Chip kind="wait">warn-only</Chip>}
                </div>,
              ];
            })}
            colWidths={["180px", "auto", "120px", "260px"]}
          />
        )}
      </Section>

      {/* 3. ENTRY */}
      <Section title="Entry conditions" subtitle="When this agent runs. What state must be true.">
        <FieldRow
          label="Direction"
          value={(entry.direction || []).map((d) => <Chip key={d}>{d}</Chip>)}
          chips
        />
        <FieldRow
          label="Required context fields"
          chips
          value={(entry.requires_context || []).length === 0
            ? <span className="muted small">(none)</span>
            : (entry.requires_context || []).map((c) => <Chip key={c} kind="context">{c}</Chip>)}
        />
        <FieldRow
          label="Required state"
          chips
          value={Object.keys(entry.requires_state || {}).length === 0
            ? <span className="muted small">(none)</span>
            : Object.entries(entry.requires_state || {}).map(([k, v]) =>
              <Chip key={k} kind="state">{k}={String(v)}</Chip>)}
        />
        <FieldRow
          label="Typically preceded by"
          chips
          value={(entry.typically_preceded_by || []).length === 0
            ? <span className="muted small">(none — first agent or independent)</span>
            : (entry.typically_preceded_by || []).map((a) => <Chip key={a} kind="agent">{a}</Chip>)}
        />
      </Section>

      {/* 4. RULES */}
      <Section title={`Rules (${rules.length})`} subtitle="Always / never. Numbered. Severity-tagged.">
        {rules.length === 0 ? (
          <div className="muted small">
            No agent-specific rules. Universal + framework rules still apply (see Combined view).
          </div>
        ) : (
          <Table
            head={["#", "ID", "Severity", "Text"]}
            rows={rules.map((r, i) => [
              <span key="n" style={{ fontFamily: "ui-monospace", fontSize: 11 }}>{i + 1}</span>,
              <span key="id" style={{ fontFamily: "ui-monospace", fontSize: 11 }}>{r.id}</span>,
              <SeverityBadge key="s" severity={r.severity} />,
              <div key="t" style={{ fontSize: 13, lineHeight: 1.5 }}>{r.text}</div>,
            ])}
            colWidths={["50px", "80px", "110px", "auto"]}
          />
        )}
      </Section>

      {/* 5. STEPS */}
      <Section title={`Steps (${steps.length})`} subtitle="Sequenced procedure. Each step is one action or pause.">
        {steps.length === 0 ? (
          <div className="muted small">No steps defined.</div>
        ) : (
          <Table
            head={["#", "ID", "Name", "Body", "Wait"]}
            rows={steps.map((s, i) => [
              <span key="n" style={{ fontFamily: "ui-monospace", fontSize: 11 }}>{i + 1}</span>,
              <span key="id" style={{ fontFamily: "ui-monospace", fontSize: 11 }}>{s.id}</span>,
              <strong key="nm" style={{ fontSize: 13 }}>{s.name}</strong>,
              <pre key="b" style={{
                margin: 0, fontFamily: "ui-monospace", fontSize: 11, lineHeight: 1.5,
                whiteSpace: "pre-wrap", wordBreak: "break-word",
              }}>{s.body}</pre>,
              s.waits_for_patient
                ? <Chip kind="wait">⏸ wait</Chip>
                : <span className="muted small">—</span>,
            ])}
            colWidths={["50px", "60px", "160px", "auto", "90px"]}
          />
        )}
      </Section>

      {/* 6. EXIT */}
      <Section title="Exit conditions" subtitle="How the agent's session ends.">
        <FieldRow
          label="Terminal tool"
          chips
          value={exit.terminal_tool ? <Chip kind="tool">{exit.terminal_tool}</Chip> : <span className="muted small">—</span>}
        />
        <FieldRow
          label="Required args"
          value={exit.required_args && Object.keys(exit.required_args).length > 0
            ? <pre style={{ margin: 0, fontSize: 11, fontFamily: "ui-monospace", whiteSpace: "pre-wrap" }}>{
                Object.entries(exit.required_args).map(([k, v]) => `${k}: ${v}`).join("\n")
              }</pre>
            : <span className="muted small">(none)</span>}
        />
        <FieldRow label="Done when" value={exit.success_criteria} multiline />
        <FieldRow label="Then (orchestrator behavior)" value={exit.on_completion} multiline />
      </Section>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// COMBINED / COMPILED VIEW — what the LLM actually receives.
//
// `title` is an optional caller-supplied label used to disambiguate the
// two compiled views in the UI:
//   • "Combined (agent only)"     — scope="agent", agent's contribution
//   • "Master Combined (LLM view)" — scope="all", full universal+framework+agent
// When omitted, the section falls back to a generic title.
// ─────────────────────────────────────────────────────────────────────
export function CompiledView({ prompt, ruleMap, title = null }) {
  const promptSectionTitle = title || "Full compiled prompt";
  const promptSectionSubtitle = title
    ? title.toLowerCase().includes("agent only") || title.toLowerCase().includes("contribution")
      ? "The agent's own rules + steps only — universal and framework rules are excluded so reviewers can see exactly what the agent author wrote."
      : "The single string sent to the LLM at agent activation, with universal + framework + agent rules merged and variables substituted."
    : "The single string sent to the LLM at agent activation, with variables and handoff context already substituted.";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {title && (
        <div style={{
          padding: "10px 14px",
          background: "#eef3fa",
          border: "1px solid #cfd8e3",
          borderRadius: 6,
          fontSize: 12,
          color: "#2E4F72",
          fontWeight: 600,
        }}>
          {title}
        </div>
      )}
      <Section
        title="Rule map"
        subtitle="How rules from each scope (universal → framework → agent) got merged and renumbered. This is the order the LLM reads them."
      >
        <Table
          head={["#", "Scope", "Original ID", "Severity"]}
          rows={(ruleMap || []).map((r) => [
            <span key="n" style={{ fontFamily: "ui-monospace", fontSize: 11, fontWeight: 600 }}>{r.rendered_n}</span>,
            <span key="s" style={{
              fontWeight: 600,
              color: r.scope === "universal" ? "#1565c0" : r.scope === "framework" ? "#7b1fa2" : "#2e7d32",
            }}>{r.scope}</span>,
            <span key="id" style={{ fontFamily: "ui-monospace", fontSize: 11 }}>{r.original_id}</span>,
            <SeverityBadge key="sv" severity={r.severity} />,
          ])}
          colWidths={["50px", "100px", "100px", "110px"]}
        />
      </Section>

      <Section
        title={promptSectionTitle}
        subtitle={promptSectionSubtitle}
      >
        <pre style={{
          margin: 0, padding: 12, background: "#fafafa",
          border: "1px solid #e5e5e5", borderRadius: 4,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: 11, lineHeight: 1.5,
          whiteSpace: "pre-wrap", wordBreak: "break-word",
          maxHeight: 720, overflowY: "auto",
        }}>
          {prompt}
        </pre>
      </Section>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// UI primitives.
// ─────────────────────────────────────────────────────────────────────
export function Section({ title, subtitle, children }) {
  return (
    <div style={{ border: "1px solid #e5e5e5", borderRadius: 6, background: "#fff" }}>
      <div style={{
        padding: "10px 14px",
        borderBottom: "1px solid #f0f0f0",
        background: "#f8f9fa",
        borderRadius: "6px 6px 0 0",
      }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#2E4F72", textTransform: "uppercase", letterSpacing: 0.6 }}>
          {title}
        </div>
        {subtitle && <div className="muted small" style={{ fontSize: 11 }}>{subtitle}</div>}
      </div>
      <div style={{ padding: 14 }}>{children}</div>
    </div>
  );
}

export function FieldRow({ label, value, multiline = false, chips = false }) {
  return (
    <div style={{ display: "flex", marginBottom: 10, gap: 12 }}>
      <div style={{
        width: 200, flexShrink: 0,
        fontSize: 11, fontWeight: 600, color: "#666",
        textTransform: "uppercase", letterSpacing: 0.6,
        paddingTop: 4,
      }}>
        {label}
      </div>
      <div style={{
        flex: 1,
        fontSize: 13, lineHeight: 1.5,
        whiteSpace: multiline ? "pre-wrap" : "normal",
        display: chips ? "flex" : "block",
        flexWrap: chips ? "wrap" : undefined,
        gap: chips ? 4 : undefined,
      }}>
        {value || <span className="muted small">—</span>}
      </div>
    </div>
  );
}

export function Table({ head, rows, colWidths }) {
  return (
    <div style={{ overflowX: "auto", border: "1px solid #e5e5e5", borderRadius: 4 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ background: "#f0f3f7" }}>
            {head.map((h, i) => (
              <th key={i} style={{
                padding: "8px 10px", textAlign: "left",
                fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5,
                color: "#2E4F72",
                borderBottom: "1px solid #d8dde3",
                width: colWidths?.[i],
              }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fafbfc" }}>
              {row.map((cell, j) => (
                <td key={j} style={{
                  padding: "8px 10px", verticalAlign: "top",
                  borderTop: i === 0 ? "none" : "1px solid #f0f0f0",
                }}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SeverityBadge({ severity }) {
  const map = {
    critical: { bg: "#ffebee", color: "#c62828", icon: "🚨", label: "CRITICAL" },
    standard: { bg: "#e8f5e9", color: "#2e7d32", icon: "",   label: "standard" },
    advisory: { bg: "#f5f5f5", color: "#666",    icon: "",   label: "advisory" },
  };
  const s = map[severity] || map.standard;
  return (
    <span style={{
      display: "inline-block",
      padding: "2px 8px", fontSize: 10, fontWeight: 700,
      letterSpacing: 0.5, textTransform: "uppercase",
      background: s.bg, color: s.color, borderRadius: 4,
      whiteSpace: "nowrap",
    }}>
      {s.icon}{s.icon ? " " : ""}{s.label}
    </span>
  );
}

export function Chip({ children, kind }) {
  const colors = {
    context: { bg: "#e3f2fd", color: "#1565c0" },
    state:   { bg: "#f3e5f5", color: "#6a1b9a" },
    agent:   { bg: "#fff3e0", color: "#ef6c00" },
    tool:    { bg: "#f5f0fa", color: "#5e35b1" },
    wait:    { bg: "#fff8e1", color: "#5b4400" },
    default: { bg: "#eceff1", color: "#37474f" },
  };
  const c = colors[kind] || colors.default;
  return (
    <span style={{
      display: "inline-block",
      padding: "2px 8px", fontSize: 11, fontWeight: 600,
      background: c.bg, color: c.color, borderRadius: 10,
      whiteSpace: "nowrap",
    }}>
      {children}
    </span>
  );
}
